import TechTreeCanvas from "./techTreeCanvas";
import TechNodeInspector from "./techNodeInspector";
import {useDrag, useDrop} from "react-dnd";
import {RemoteConstantsModel} from "../../models/remoteConstants.model";
import {useIntegrator} from "../../integrator.context";
import {RemoveTechUnlockCommand} from "../../commands/removeTechUnlock.command";
import EntityLabel from "../../elements/entityLabel";
import MetadataDragLabel from "../../elements/metadataDragLabel";
import {useApp} from "../../app.context";
import {getDuplicateIds} from "../../utils/metadata.utils";
import {MetadataCollection} from "../../commands/metadataValue.command";
import PriceList from "./prices";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import {useNavTarget} from "../../elements/useNavTarget";
import {NavTarget} from "../../utils/navTarget";
import {useRef, useState} from "react";

type AssetTab = 'Units' | 'Buildings' | 'Actions' | 'Passives';

function TechTreeEditorView() {
    // Resizable palette: a vertical bar drags the column width, a horizontal
    // bar splits the Prices (top) and Assets (bottom) sections. See
    // elements/resizeBar; no collapse buttons here.
    const containerRef = useRef<HTMLDivElement>(null);
    const paletteRef = useRef<HTMLDivElement>(null);
    const inspectorRef = useRef<HTMLDivElement>(null);
    const palette = useResizable({
        storageKey: 'rift.editor.techTreePaletteWidth',
        axis: 'x', min: 240, invert: true,                      // palette sits right of the bar
        initial: () => Math.round(window.innerWidth * 0.25),    // old fixed 25vw
        // Leave the tree canvas at least this wide.
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 320,
    });
    const prices = useResizable({
        storageKey: 'rift.editor.techTreePricesHeight',
        axis: 'y', min: 80, initial: 240,                       // Prices sits above the bar
        // Reserve the bar + gaps + the Assets minimum at the bottom, and the
        // inspector above: it's always on screen and its height varies with the
        // selected node's unlock count, so it's measured rather than assumed.
        max: () => (paletteRef.current?.getBoundingClientRect().height ?? Infinity)
            - (inspectorRef.current?.getBoundingClientRect().height ?? 0) - 140,
    });

    // Selection is owned here so the canvas and the inspector agree on it.
    const [selected, setSelected] = useState<string | undefined>(undefined);

    return (
        // minHeight: 0 pins the tab to the viewport row (as the Maps tab does):
        // without it the palette's fixed-height children set a content-based
        // floor, the whole tab scrolls, and the canvas stretches past the window.
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1, minHeight: 0}}>
            <div className={'layout center flex-grow'} style={{position: 'relative', overflow: 'hidden', borderRadius: 'var(--border-radius)', border: 'solid 1px var(--overlay-color)', minWidth: 0}}>
                <TechTreeCanvas selected={selected} onSelect={setSelected}/>
            </div>

            {/* Draggable bar to resize the palette width (drags horizontally). */}
            <ResizeBar axis={'x'} {...palette.handleProps}/>

            {/* Bounded and self-scrolling: the inspector is auto-height (it grows
                with the selected node's unlock count) and Prices is a fixed drag
                height, so a short window can leave the two of them taller than
                the column. Prices yields down to its drag minimum first; only if
                that isn't enough does the column scroll. */}
            <div ref={paletteRef} className={'layout vertical'}
                 style={{width: palette.size, flexShrink: 0, minHeight: 0, overflowY: 'auto'}}>
                {/* Inspector for the selected node; auto-height, above Prices.
                    Always present — with nothing selected it shows Root, which
                    is the only place root is editable now the canvas omits it. */}
                <div ref={inspectorRef} style={{flexShrink: 0, paddingBottom: 4}}>
                    <TechNodeInspector id={selected} onSelect={setSelected}/>
                </div>

                {/* flexShrink lets Prices give space back when the column is
                    short, down to the same minimum the drag enforces — so the
                    Assets list below stays usable instead of being squeezed out. */}
                <div style={{height: prices.size, flexShrink: 1, minHeight: 80, overflowY: 'auto'}}>
                    <PriceList/>
                </div>

                {/* Draggable separator between the Prices and Assets sections. */}
                <ResizeBar axis={'y'} {...prices.handleProps}/>

                <AssetsList/>
            </div>
        </div>
    );
}

function AssetsList() {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue} = useApp();
    const actionDupes = getDuplicateIds(state.techActionAttributes ?? []);
    const passiveDupes = getDuplicateIds(state.techPassiveAttributes ?? []);
    const [tab, setTab] = useState<AssetTab>('Units');

    const [{canDrop}, drop] = useDrop({
        accept: 'entity',
        drop: (item: any) => executeCommand(new RemoveTechUnlockCommand(item.nodeId, item.id)),
        collect: (monitor) => {
            return {
                canDrop: monitor.isOver({shallow: true})
            };
        },
    });

    return (
        <div ref={drop} className={'layout vertical gap box'} style={{
            height: 0,
            flexGrow: 1,
            overflowY: 'auto',
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <div className={'layout horizontal gap'} style={{width: '100%'}}>
                <AssetTabButton label={'Units'} current={tab} onClick={setTab}/>
                <AssetTabButton label={'Buildings'} current={tab} onClick={setTab}/>
                <AssetTabButton label={'Actions'} current={tab} onClick={setTab}/>
                <AssetTabButton label={'Passives'} current={tab} onClick={setTab}/>
            </div>
            <div className={'layout vertical gap'} style={{
                flexGrow: 1,
                overflowY: 'auto'
            }}>
                {tab === 'Units' && [...state.units]
                    .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                    .map((unit, i) => <EntityDragLabel key={i} id={unit.id} navTarget={{kind: 'unit', id: unit.id}} suffix={assignedNodeSuffix(state, unit.id, getLocalizationValue)}/>)}
                {tab === 'Buildings' && [...state.buildings]
                    .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                    .map((building, i) => <EntityDragLabel key={i} id={building.id} navTarget={{kind: 'building', id: building.id}} suffix={assignedNodeSuffix(state, building.id, getLocalizationValue)}/>)}
                {tab === 'Actions' && state.techActionAttributes && [...state.techActionAttributes]
                    .sort((a, b) => getLocalizationValue(a.uid).localeCompare(getLocalizationValue(b.uid)))
                    .map((metadata, index) => <MetadataDragLabel key={index} uid={metadata.uid} id={metadata.id} type={'entity'} values={metadata.values} entities={metadata.entities} collection={MetadataCollection.TechActions} duplicate={actionDupes.has(metadata.id)} draggable={() => !alreadyApplied(state, metadata.uid)} suffix={assignedNodeSuffix(state, metadata.uid, getLocalizationValue)}/>)}
                {tab === 'Passives' && state.techPassiveAttributes && [...state.techPassiveAttributes]
                    .sort((a, b) => getLocalizationValue(a.uid).localeCompare(getLocalizationValue(b.uid)))
                    .map((metadata, index) => <MetadataDragLabel key={index} uid={metadata.uid} id={metadata.id} type={'entity'} values={metadata.values} entities={metadata.entities} collection={MetadataCollection.TechPassives} duplicate={passiveDupes.has(metadata.id)} draggable={() => !alreadyApplied(state, metadata.uid)} suffix={assignedNodeSuffix(state, metadata.uid, getLocalizationValue)}/>)}
            </div>
        </div>
    )
}

function AssetTabButton(props: {label: AssetTab, current: AssetTab, onClick: (t: AssetTab) => void}) {
    const selected = props.label === props.current;
    return (
        <button
            className={`layout horizontal center stretch bar-state ${selected ? 'bar-state-active' : 'bar-state-inactive'}`}
            style={{flexGrow: 1}}
            onClick={() => props.onClick(props.label)}>
            {props.label}
        </button>
    );
}

function EntityDragLabel(props: {id: string, suffix?: string, navTarget?: NavTarget}) {
    const {state} = useIntegrator();
    const {setNotification} = useApp();

    const [{isDragging}, drag] = useDrag({
        type: 'entity',
        item: {
            id: props.id,
        },
        collect: (monitor) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        canDrag: !alreadyApplied(state, props.id)
    });

    const style = {
        opacity: isDragging || alreadyApplied(state, props.id) ? 0.5 : 1,
    };

    // Pass the building's startLevel for building unlocks so the image
    // resolves through the pack (matches the Tiles tab palette). For non-
    // buildings (units / tech attributes), level defaults to 0.
    const buildingData = state.buildings.find(b => b.id === props.id);

    return (
        <div ref={drag} {...useNavTarget(props.navTarget)} style={style} onClick={() => {
            setNotification({
                title: props.id,
                message: props.id + '.description',
                edit: true
            })
        }}>
            <EntityLabel id={props.id} level={buildingData?.startLevel} suffix={props.suffix}/>
        </div>
    );
}

function alreadyApplied(remoteConstants: RemoteConstantsModel, unlock: string): boolean {
    return remoteConstants.techTree.some(t =>
        t.unlocks !== undefined && t.unlocks !== null && t.unlocks.some(a => a === unlock));
}

// Find the first tech node whose `unlocks` array contains the given id/uid,
// and return a localized-name suffix in the shape " (NodeName)". Empty when
// the entity isn't unlocked by any node. Used by the Tech Tree tab's right-hand
// list so designers see at a glance which node carries each entity.
function assignedNodeSuffix(
    remoteConstants: RemoteConstantsModel,
    unlock: string,
    getLocalizationValue: (key: string) => string
): string {
    const node = remoteConstants.techTree.find(t =>
        t.unlocks !== undefined && t.unlocks !== null && t.unlocks.some(a => a === unlock));
    return node ? ` (${getLocalizationValue(node.id)})` : '';
}

export default TechTreeEditorView;